import React, { useEffect, useRef, useState } from "react";
import "../../../src/assets/styles/polylect-home.scss";

const overviewItems = [
    {
        icon: "fa-solid fa-language",
        title: "Wiele języków",
        text: "Czytaj teksty w kilku językach jednocześnie – m.in. po angielsku, niemiecku, hiszpańsku, włosku, francusku i ukraińsku – wraz z tłumaczeniami.",
    },
    {
        icon: "fa-solid fa-bolt",
        title: "Szybka nauka",
        text: "Krótkie teksty, quizy i własne listy słówek pomagają uczyć się języka małymi porcjami – idealnie w drodze do pracy, w tramwaju lub w przerwie od innych zajęć.",
    },
    {
        icon: "fa-solid fa-lock",
        title: "Prywatność i kontrola",
        text: "Możesz korzystać jako gość lub założyć konto, żeby zapisywać postępy. Dane powiązane z kontem są przechowywane zgodnie z Polityką Prywatności, a konto możesz usunąć w każdej chwili.",
    },
];

const leftFeatures = [
    {
        title: "Tryb gościa",
        text: "Zacznij bez rejestracji – przeglądaj wybrane darmowe teksty i odsłuchuj nagrania lektora.",
    },
    {
        title: "Konto użytkownika",
        text: "Załóż konto e-mailem lub zaloguj się przez Google, aby synchronizować ulubione teksty, listy słówek i statystyki nauki między maksymalnie trzema urządzeniami.",
    },
    {
        title: "Statystyki nauki",
        text: "Śledź liczbę przeczytanych tekstów, rozwiązanych quizów i zapamiętanych słówek. Zobacz, jak rosną Twoje postępy z dnia na dzień.",
    },
];

const rightFeatures = [
    {
        title: "Nagrania audio",
        text: "Odtwarzaj nagrania lektora do wybranych tekstów i osłuchuj się z językiem.",
    },
    {
        title: "Tłumacz słówek",
        text: "Przytrzymaj dłużej słowo w tekście, aby przetłumaczyć je przez Google Translate. Idealne, gdy trafisz na nowe słówko w trakcie lektury.",
    },
    {
        title: "Tryb dwujęzykowy",
        text: "Kliknij w przycisk aby czytać w dwóch językach, lub skorzystaj z lupy aby przetłumaczyć dany fragment tekstu.",
    },
];

const faqItems = [
    {
        question: "Czy aplikacja jest darmowa?",
        answer:
            "Aplikacja jest darmowa do pobrania, a część treści pozostaje dostępna bezpłatnie w trybie gościa lub po założeniu konta. Polylect Premium odblokowuje dostęp do większości tekstów oraz dodatkowe funkcje, takie jak pełne statystyki nauki.",
    },
    {
        question: "Jak zarządzać subskrypcją Polylect Premium?",
        answer:
            "Zakup i rozliczenia odbywają się przez App Store lub Google Play. Subskrypcją zarządzasz w ustawieniach odpowiedniego sklepu. Usunięcie konta w aplikacji nie anuluje automatycznie subskrypcji w sklepie.",
    },
    {
        question: "Czy treści w Polylect są w 100% bezbłędne?",
        answer:
            "Treści w Polylect są przygotowane z myślą o nauce w praktyce i regularnie udoskonalane. Sporadycznie mogą zdarzyć się drobne błędy — aplikacja służy do nauki i nie zastępuje tłumaczeń przysięgłych ani formalnych.",
    },
];

export default function Home() {
    const [categoriesCount, setCategoriesCount] = useState(0);
    const [textsCount, setTextsCount] = useState(0);
    const [languageCount, setLanguageCount] = useState(0);
    const rootRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const elements = rootRef.current?.querySelectorAll(".reveal");

        if (!elements || elements.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.16,
                rootMargin: "0px 0px -60px 0px",
            }
        );

        elements.forEach((element) => observer.observe(element));

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const duration = 900;
        const start = performance.now();

        let animationFrameId = 0;

        const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);

            setCategoriesCount(Math.floor(progress * 15));
            setTextsCount(Math.floor(progress * 170));
            setLanguageCount(Math.floor(progress * 6));

            if (progress < 1) {
                animationFrameId = requestAnimationFrame(animate);
            }
        };

        animationFrameId = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrameId);
    }, []);

    return (
        <div ref={rootRef} className="home-dashboard">
            <header className="header" id="home">
                <div className="overlay"></div>
                <div className="header__grid" aria-hidden="true"></div>
                <div className="header__glow header__glow--one" aria-hidden="true"></div>
                <div className="header__glow header__glow--two" aria-hidden="true"></div>

                <nav className="navbar container" aria-label="Nawigacja główna">
                    <div className="navbar__logo">
                        <span className="logo__icon">
                            <img src="/splash-icon.png" alt="Polylect logo" />
                        </span>
                        <span className="logo__text">Polylect</span>
                    </div>

                    <input
                        type="checkbox"
                        id="nav-toggle"
                        className="navbar__toggle-checkbox"
                    />
                    <label htmlFor="nav-toggle" className="navbar__toggle" aria-label="Menu">
                        <span></span>
                        <span></span>
                        <span></span>
                    </label>

                    <ul className="navbar__links">
                        <li><a href="#home">Start</a></li>
                        <li><a href="#about">Jak działa</a></li>
                        <li><a href="#features">Funkcje</a></li>
                        <li><a href="#faq">FAQ</a></li>
                        <li><a href="#contact">Kontakt</a></li>
                    </ul>
                </nav>

                <section className="hero container">
                    <div className="hero__content">
                        <h1 className="hero__title animate-in delay-2">
                            Czytaj krótkie teksty
                            <br />
                            i ucz się języka w Polylect
                        </h1>

                        <p className="hero__subtitle animate-in delay-3">
                            Polylect to mobilna aplikacja językowa z tekstami w językach:
                            angielskim, niemieckim, hiszpańskim, włoskim, francuskim i ukraińskim.
                            Czytaj, słuchaj lektora, rozwiązuj quizy, buduj własne listy słówek
                            i sprawdzaj statystyki.
                        </p>

                        <div className="hero__buttons animate-in delay-4" id="download">
                            <a
                                href="https://play.google.com/store/apps/details?id=com.cia3.czytajpo&hl=pl"
                                className="googleplay"
                                aria-label="Pobierz z Google Play"
                            >
                                <img
                                    src="https://play.google.com/intl/en_us/badges/static/images/badges/pl_badge_web_generic.png"
                                    alt="Pobierz z Google Play"
                                    loading="lazy"
                                />
                            </a>

                            <a
                                href="https://apps.apple.com/pl/app/polylect/id6757873192?l=pl"
                                className="store-badge store-badge--apple"
                                aria-label="Pobierz z App Store"
                            >
                                <img
                                    src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/pl-pl?size=250x83"
                                    alt="Pobierz w App Store"
                                    loading="lazy"
                                />
                            </a>
                        </div>

                        <div className="hero__stats animate-in delay-5">
                            <div className="hero__stat">
                                <strong>{languageCount}</strong>
                                <span>języków</span>
                            </div>
                            <div className="hero__stat">
                                <strong>{categoriesCount}</strong>
                                <span>kategorii</span>
                            </div>
                            <div className="hero__stat">
                                <strong>{textsCount}+</strong>
                                <span>tekstów</span>
                            </div>
                        </div>
                    </div>

                    <div className="hero__mockups animate-in delay-4" aria-label="Podgląd aplikacji">
                        <span className="mockup-orb mockup-orb--one" aria-hidden="true"></span>
                        <span className="mockup-orb mockup-orb--two" aria-hidden="true"></span>

                        <div className="hero__chip hero__chip--left">Quizy i słówka</div>
                        <div className="hero__chip hero__chip--right">Audio lektora</div>
                        <div className="hero__chip hero__chip--bottom">Tryb dwujęzykowy</div>

                        <div className="phone phone--front">
                            <div className="phone__screen">
                                <img
                                    src="/8-portrait.png"
                                    className="phoneimg"
                                    alt="Mockup aplikacji Polylect"
                                />
                            </div>
                        </div>

                        <div className="phone phone--back">
                            <div className="phone__screen phone__screen--secondary">
                                <img
                                    src="/10.png"
                                    className="phoneimg"
                                    alt="Mockup aplikacji Polylect"
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </header>

            <main>
                <section className="section overview" id="about">
                    <div className="container">
                        <header className="section__header reveal">
                            <h2 className="section__title">Jak działa Polylect</h2>
                            <p className="section__subtitle">
                                Pobierz za darmo, czytaj bezpłatne teksty i poznaj Polylect.
                                Więcej treści odblokujesz w każdej chwili przez subskrypcję
                                Premium w App Store lub Google Play.
                            </p>
                        </header>

                        <div className="overview__grid">
                            {overviewItems.map((item, index) => (
                                <article
                                    key={item.title}
                                    className={`card overview__item reveal delay-${index + 1}`}
                                >
                                    <div className="card__icon">
                                        <i className={item.icon} aria-hidden="true"></i>
                                    </div>
                                    <h3 className="card__title">{item.title}</h3>
                                    <p className="card__text">{item.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="section advanced-features" id="features">
                    <div className="container">
                        <header className="section__header reveal">
                            <h2 className="section__title">Funkcje Polylect</h2>
                            <p className="section__subtitle">
                                W Polylect znajdziesz krótkie teksty z wielu kategorii
                                tematycznych – od życia codziennego po kulturę, podróże i
                                biznes. Czytaj, ucz się słownictwa w kontekście i rozwijaj
                                język krok po kroku.
                            </p>
                        </header>

                        <div className="advanced-features__layout">
                            <div className="advanced-features__list advanced-features__list--left">
                                {leftFeatures.map((item, index) => (
                                    <article
                                        key={item.title}
                                        className={`feature-item reveal delay-${index + 1}`}
                                    >
                                        <h3 className="feature-item__title">{item.title}</h3>
                                        <p className="feature-item__text">{item.text}</p>
                                    </article>
                                ))}
                            </div>

                            <div className="advanced-features__phone reveal delay-2">
                                <div className="phone phone--center">
                                    <img
                                        src="/11-portrait.png"
                                        className="phoneimg"
                                        alt="Mockup aplikacji Polylect"
                                    />
                                </div>
                            </div>

                            <div className="advanced-features__list advanced-features__list--right">
                                {rightFeatures.map((item, index) => (
                                    <article
                                        key={item.title}
                                        className={`feature-item reveal delay-${index + 2}`}
                                    >
                                        <h3 className="feature-item__title">{item.title}</h3>
                                        <p className="feature-item__text">{item.text}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="section faq" id="faq">
                    <div className="container faq__layout">
                        <div className="faq__image reveal">
                            <div className="phone phone--faq">
                                <div className="phone__screen phone__screen--image">
                                    <img
                                        src="/14.png"
                                        className="phoneimg"
                                        alt="Mockup aplikacji Polylect"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="faq__content">
                            <header className="section__header section__header--left reveal">
                                <h2 className="section__title">Najczęstsze pytania</h2>
                                <p className="section__subtitle">
                                    Kilka szybkich odpowiedzi na pytania, które najczęściej
                                    pojawiają się przy pierwszym kontakcie z Polylect.
                                </p>
                            </header>

                            <div className="faq__list">
                                {faqItems.map((item, index) => (
                                    <article
                                        key={item.question}
                                        className={`faq-item faq-item--static reveal delay-${index + 1}`}
                                    >
                                        <h3 className="faq-item__question">
                                            {item.question}
                                        </h3>

                                        <div className="faq-item__answer-wrapper">
                                            <p className="faq-item__answer">{item.answer}</p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="section support" id="contact">
                    <div className="container">
                        <div className="support__card reveal">
                            <header className="section__header">
                                <h2 className="section__title">Kontakt</h2>
                                <p className="section__subtitle">
                                    Masz pytanie, sugestię albo chcesz zgłosić problem? Napisz do nas,
                                    a wrócimy z odpowiedzią tak szybko, jak to możliwe.
                                </p>
                            </header>

                            <div className="support__actions">
                                <a className="btn btn--dark" href="mailto:support@polylect.pl">
                                    support@polylect.pl
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="footer">
                <div className="container footer__content">
                    <p className="footer__copy">© Polylect. Wszystkie prawa zastrzeżone.</p>

                    <div className="footer__links">
                        <a href="https://polylect.pl/regulamin">Regulamin</a>
                        <span aria-hidden="true">•</span>
                        <a href="https://polylect.pl/polityka-prywatnosci">Polityka prywatności</a>
                    </div>

                    <p className="footer__credit">
                        Kontakt: <a href="mailto:support@polylect.pl">support@polylect.pl</a>
                    </p>
                </div>
            </footer>
        </div>
    );
}